import React, { useState, useEffect, useRef } from 'react';
import { useAuth, ROLES } from './AuthContext';
import { supabase } from './supabaseClient';
import { parseDbTime, parseWindowTime } from './utils/dbTime';   // __TZ_WINDOWS__
import KanbanBoard, { jobParam } from './components/KanbanBoard';
import DashboardStats from './components/DashboardStats';
import TruckManagementModal from './components/TruckManagementModal';
import TrailerManagementModal from './components/TrailerManagementModal';
import FleetCombinationsModal from './components/FleetCombinationsModal';
import DriverManagementModal from './components/DriverManagementModal';
import SwapPreviewModal from './components/SwapPreviewModal';
import FleetMapModal from './components/FleetMapModal';
import AlertInbox from './components/AlertInbox';
import SettingsModal from './components/SettingsModal';
import NotificationCenter from './components/NotificationCenter';
import RedAlertPanel from './components/RedAlertPanel';
import TimelineView from './components/TimelineView';
import DayGraphView from './components/DayGraphView';   // __DAY_GRAPH__
import RouteBuilder from './components/RouteBuilder';
import PlanningBoard from './components/PlanningBoard';   // __PLANNING__
import CompletedJobsArchive from './components/CompletedJobsArchive';
import MaintenanceModal from './components/MaintenanceModal';
import MaintenanceView from './components/MaintenanceView';
import FleetTrackingView from './components/FleetTrackingView'; // __FLEET_TRACKING__
import EquipmentAssignmentView from './components/EquipmentAssignmentView'; // __EQUIP_ASSIGN__
import ReportsModal from './components/ReportsModal';
import FuelStopModal from './components/FuelStopModal';
import { isGPSConfigured } from './services/gpsTrackingService';
import { isWhatsAppConfigured } from './services/whatsappService';
import { isAIConfigured } from './services/aiDispatchService';
import { getUnprocessedMessages } from './services/whatsappWebhookService';
import { startAutoGeocoding } from './services/geocodingService';
import { loadSharedMapsKey } from './services/mapsKeyService';
import { redirectToBilling } from './utils/auth';
import { subscribeToGeofenceEvents, formatGeofenceAlert } from './services/geofenceAlertsService';
import { countInbox, subscribeInbox, describeGeofenceEvent } from './services/alertInboxService';
import { subscribeStopFixRouteRefresh } from './services/stopFixRouteRefresh';
import { subscribeToBreaks, formatBreakStatus } from './services/breakMonitorService';
import DispatcherActivityModal from './components/DispatcherActivityModal';
import BIDashboardModal from './components/BIDashboardModal';
import { fetchRules } from './services/equipmentService';           // __EQUIP_V2__
import { halfLoadOn, todayISO } from './utils/equipmentCompat';      // __EQUIP_V2__
import {
  getDefaultUnitSystem,
  formatLength,
  formatWeight,
  formatArea,
  formatVolume,
  getLengthUnit,
  getWeightUnit,
  getAreaUnit,
  getVolumeUnit
} from './utils/units';

function Dashboard() {
  const { user, signOut, profile, role, isOwner } = useAuth();

  const [showTruckModal, setShowTruckModal] = useState(false);
  const [showTrailerModal, setShowTrailerModal] = useState(false);
  const [showCombinationsModal, setShowCombinationsModal] = useState(false);
  const [showDriverModal, setShowDriverModal] = useState(false);
  const [showSwapPreview, setShowSwapPreview] = useState(false);
  const [showFleetMap, setShowFleetMap] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showNotificationCenter, setShowNotificationCenter] = useState(false);
  const [showRedAlertPanel, setShowRedAlertPanel] = useState(false);
  const [showMaintenanceModal, setShowMaintenanceModal] = useState(false);
  const [showFuelStopModal, setShowFuelStopModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showBIDashboard, setShowBIDashboard] = useState(false);

  const [currentView, setCurrentView] = useState('kanban');
  // __DAY_GRAPH__ the board view (Kanban / Timeline / Day graph) is remembered for each login on this browser
  const BOARD_VIEWS = ['kanban', 'timeline', 'daygraph', 'planning'];
  const viewKey = 'hb_board_view_' + ((user && user.id) || '');
  // __PORTAL_JUMP__ ?job=BK-... (the hub's "Hauling board" button) opens on Kanban, where the job opens; the view
  // remembered for this login is left as it was
  const jumpView = useRef(jobParam(window.location.search) ? 'kanban' : null);
  useEffect(() => {
    if (!user || !user.id || jumpView.current) return;
    try { const v = localStorage.getItem('hb_board_view_' + user.id); if (BOARD_VIEWS.includes(v)) setCurrentView(v); } catch (e) { /* a convenience */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user && user.id]);
  useEffect(() => {
    if (!user || !user.id || !BOARD_VIEWS.includes(currentView)) return;
    if (jumpView.current && currentView === jumpView.current) return;   // __PORTAL_JUMP__
    jumpView.current = null;
    try { localStorage.setItem(viewKey, currentView); } catch (e) { /* a convenience */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentView, viewKey]);
  // __PLANNING_ASSIGN__ "Open Route Builder" from the Planning board's status line or a Needs-attention alert
  useEffect(() => {
    const go = (e) => { const v = e && e.detail && e.detail.view; if (v) setCurrentView(v); };
    window.addEventListener('hb-open-view', go);
    return () => window.removeEventListener('hb-open-view', go);
  }, []);
  const [statusMessage, setStatusMessage] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [unprocessedCount, setUnprocessedCount] = useState(0);
  const [customers, setCustomers] = useState([]);
  const [geofenceAlert, setGeofenceAlert] = useState(null);
  const [showAlertInbox, setShowAlertInbox] = useState(false);
  const [inboxCount, setInboxCount] = useState(0);

  // Fetch customers for Red Alert Panel
  useEffect(() => {
    const fetchCustomers = async () => {
      const { data } = await supabase.from('customers').select('*');
      if (data) setCustomers(data);
    };
    fetchCustomers();
  }, []);

  // Check for unprocessed messages periodically
  useEffect(() => {
    const checkMessages = () => {
      const messages = getUnprocessedMessages();
      setUnprocessedCount(messages.length);
    };
    checkMessages();
    const interval = setInterval(checkMessages, 10000);
    return () => clearInterval(interval);
  }, []);

  // __BOARD_MAPS_KEY__ fetch the shared Google Maps browser key once, so every
  // map / route / geocode on this computer uses it (mapsKeyService caches it).
  useEffect(() => { loadSharedMapsKey(true).catch(() => {}); }, []);

  // Auto-geocoding
  useEffect(() => {
    const stopGeocoding = startAutoGeocoding();
    return () => stopGeocoding();
  }, []);

  // Geofence alerts — no blocking window.alert. The realtime payload has no
  // joins, so the toast names the load by number after a quick lookup; the
  // full, acknowledgeable list lives in the Alert Inbox.
  useEffect(() => {
    const unsubGeofence = subscribeToGeofenceEvents(async (event) => {
      const alert = formatGeofenceAlert(event);
      console.log(`[GEOFENCE] ${alert.icon} ${alert.title}: ${alert.detail}`);
      if (['dwell', 'completion_pending', 'group_open', 'drive_by', 'gps_gap'].includes(event.event_type)) {
        let enriched = event;
        try {
          const [{ data: drv }, { data: ld }] = await Promise.all([
            event.driver_id ? supabase.from('drivers').select('id, user:user_id(name)').eq('id', event.driver_id).single() : { data: null },
            event.load_id ? supabase.from('loads').select('id, load_number, status').eq('id', event.load_id).single() : { data: null },
          ]);
          enriched = { ...event, driver: drv, load: ld };
        } catch (e) { /* toast still shows without names */ }
        const d = describeGeofenceEvent(enriched);
        setGeofenceAlert(`${d.icon} ${d.title}`);
        setTimeout(() => setGeofenceAlert(null), 10000);
      }
    });
    return () => unsubGeofence();
  }, []);

  // __EQUIP_V2__ "Half-load season in effect" badge (Settings -> Equipment rules -> Half-load season)
  const [halfLoad, setHalfLoad] = useState(null);
  useEffect(() => {
    let alive = true;
    const look = () => fetchRules().then((r) => { if (alive) setHalfLoad(halfLoadOn(r, todayISO())); }).catch(() => {});
    look();
    const t = setInterval(look, 30 * 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // Alert inbox badge: unacknowledged geofence events + unread notifications
  useEffect(() => {
    let alive = true;
    const refreshCount = async () => { const n = await countInbox(); if (alive) setInboxCount(n); };
    refreshCount();
    const unsub = subscribeInbox(() => refreshCount());
    const t = setInterval(refreshCount, 60000);
    return () => { alive = false; unsub(); clearInterval(t); };
  }, []);

  // __STOP_FIX_ROUTE__ a driver fixed a stop address (Driver app 1.24, via the hub): refresh his route here
  useEffect(() => subscribeStopFixRouteRefresh(), []);

  // Break monitoring
  useEffect(() => {
    const unsubBreaks = subscribeToBreaks((eventType, newBreak) => {
      const status = formatBreakStatus(newBreak);
      if (eventType === 'INSERT') {
        console.log('[BREAK] ' + status.icon + ' ' + status.title + ': ' + status.detail);
      } else if (eventType === 'UPDATE' && newBreak.status !== 'active') {
        console.log('[BREAK] Break ended: ' + (newBreak.end_reason || newBreak.status));
      }
    });
    return () => unsubBreaks();
  }, []);

  const handleLogout = async () => {
    await signOut();
  };

  const handleExportLoads = async () => {
    setExporting(true);
    setStatusMessage(null);
    try {
      const unitSystem = getDefaultUnitSystem();
      const lengthUnit = getLengthUnit(unitSystem);
      const weightUnit = getWeightUnit(unitSystem);
      const areaUnit = getAreaUnit(unitSystem);
      const volumeUnit = getVolumeUnit(unitSystem);

      const { data: loads, error } = await supabase
        .from('loads')
        .select(`
          *,
          customer:customers(company_name, phone),
          driver:drivers(user_id(name), phone),
          truck:trucks(truck_number, license_plate),
          load_stops(id, stop_type, stop_sequence, address, time_window_start, time_window_end, actual_arrival, actual_departure, notes)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      if (!loads || loads.length === 0) {
        setStatusMessage({ type: 'info', text: 'No loads to export' });
        setExporting(false);
        return;
      }

      const maxStops = Math.max(...loads.map(load => {
        return (load.load_stops || []).sort((a, b) => {
          if (a.stop_type !== b.stop_type) return a.stop_type === 'pickup' ? -1 : 1;
          return a.stop_sequence - b.stop_sequence;
        }).length;
      }), 1);

      const stopHeaders = Array.from({ length: maxStops }, (_, i) => `Stop ${i + 1} Address`);

      const headers = [
        'Load Number', ...stopHeaders,
        `Weight (${weightUnit})`, `Length (${lengthUnit})`, `Width (${lengthUnit})`,
        `Height (${lengthUnit})`, `Area (${areaUnit})`, `Volume (${volumeUnit})`,
        'Customer Name', 'Booker Name', 'Driver Name', 'Unexpected Delays (min)',
        'Status', 'Commodity', 'Notes'
      ];

      const rows = loads.map(load => {
        const allStops = (load.load_stops || []).sort((a, b) => {
          if (a.stop_type !== b.stop_type) return a.stop_type === 'pickup' ? -1 : 1;
          return a.stop_sequence - b.stop_sequence;
        });
        const stopAddresses = Array.from({ length: maxStops }, (_, i) => allStops[i]?.address || '');

        let unexpectedDelays = 0;
        allStops.forEach(stop => {
          // __TZ_WINDOWS__ the two columns are NOT the same kind: actual_arrival is naive
          // UTC (parseDbTime), time_window_end is Toronto wall clock (parseWindowTime).
          // Raw new Date() on both read the arrival 4-5 h late against the window.
          const _arr = stop.actual_arrival ? parseDbTime(stop.actual_arrival) : null;
          const _wEnd = stop.time_window_end ? parseWindowTime(stop.time_window_end) : null;
          if (_arr && _wEnd) {
            const late = _arr - _wEnd;
            if (late > 0) unexpectedDelays += Math.round(late / 60000);
          }
          if (stop.actual_arrival && stop.actual_departure) {
            const dwell = (new Date(stop.actual_departure) - new Date(stop.actual_arrival)) / 60000;
            if (dwell > 30) unexpectedDelays += Math.round(dwell - 30);
          }
        });

        return [
          load.load_number || '', ...stopAddresses,
          formatWeight(load.weight_kg, unitSystem),
          formatLength(load.length_cm, unitSystem),
          formatLength(load.width_cm, unitSystem),
          formatLength(load.height_cm, unitSystem),
          formatArea(load.length_cm, load.width_cm, unitSystem),
          formatVolume(load.length_cm, load.width_cm, load.height_cm, unitSystem),
          load.customer?.company_name || '',
          load.booker_name || '',
          load.driver?.user_id?.name || '',
          unexpectedDelays > 0 ? unexpectedDelays : '',
          load.status || '',
          load.commodity || '',
          (load.notes || '').replace(/"/g, '""')
        ];
      });

      const escapeCSV = (v) => {
        const s = String(v);
        return (s.includes(',') || s.includes('"') || s.includes('\n')) ? `"${s.replace(/"/g, '""')}"` : s;
      };

      const csv = [headers.join(','), ...rows.map(r => r.map(escapeCSV).join(','))].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `jobs_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setStatusMessage({ type: 'success', text: `Exported ${loads.length} jobs (${unitSystem})` });
    } catch (error) {
      console.error('Export error:', error);
      setStatusMessage({ type: 'error', text: `Export failed: ${error.message}` });
    } finally {
      setExporting(false);
    }
  };

  const btnStyle = {
    padding: '0.6rem 1rem',
    backgroundColor: 'var(--btn-secondary-bg)',
    color: 'var(--text-primary)',
    border: '1px solid var(--border-default)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    fontWeight: '500',
    fontSize: '0.9rem',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem'
  };

  const viewBtnStyle = (view) => ({
    padding: '0.5rem 1rem',
    backgroundColor: currentView === view ? 'var(--btn-primary-bg)' : 'transparent',
    color: 'var(--text-primary)',
    border: currentView === view ? 'none' : '1px solid var(--border-default)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    fontWeight: currentView === view ? '600' : '400',
    fontSize: '0.85rem'
  });

  const warningDot = (
    <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: 'var(--color-warning)', flexShrink: 0 }} />
  );

  return (
    <div className="dashboard-container" style={{
      minHeight: '100vh',
      backgroundColor: 'var(--bg-primary)',
      color: 'var(--text-primary)',
      padding: '2rem'
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>

        {/* Geofence alert banner */}
        {geofenceAlert && (
          <div style={{
            marginBottom: '1rem',
            padding: '0.75rem 1rem',
            backgroundColor: 'var(--color-warning)',
            color: '#000',
            borderRadius: 'var(--radius-md)',
            fontSize: '0.9rem',
            fontWeight: '500',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>⚠ {geofenceAlert}</span>
            <button onClick={() => setGeofenceAlert(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem' }}>✕</button>
          </div>
        )}

        {/* Header */}
        <div className="dashboard-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '2rem',
          padding: '1.5rem',
          backgroundColor: 'var(--surface-1)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border-default)'
        }}>
          <div>
            <h1 style={{ fontSize: '2rem', margin: '0 0 0.5rem 0', color: 'var(--text-primary)' }}>
              HM Hauling Fleet Command Center
            </h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <p style={{ color: 'var(--text-secondary)', margin: 0 }}>
                Welcome, {profile?.name || user?.email}
              </p>
              {role && (
                <span style={{
                  padding: '0.25rem 0.75rem',
                  backgroundColor: role === ROLES.OWNER ? 'var(--color-danger)' : 'var(--accent-blue)',
                  color: 'var(--text-primary)',
                  borderRadius: '15px',
                  fontSize: '0.75rem',
                  fontWeight: 'bold',
                  textTransform: 'uppercase'
                }}>
                  {role}
                </span>
              )}
              {halfLoad && (
                <span data-half-load-badge title={'Jobs dated ' + halfLoad.start + ' to ' + halfLoad.end + ' are checked against the season limit'
                  + (halfLoad.note ? ' - ' + halfLoad.note : '') + '. Settings -> Equipment rules.'}
                  style={{ padding: '0.25rem 0.75rem', backgroundColor: 'var(--color-warning)', color: '#000', borderRadius: '15px', fontSize: '0.75rem', fontWeight: 'bold' }}>
                  Half-load season in effect (to {halfLoad.end})
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <button
              onClick={() => redirectToBilling(role)}
              style={{ ...btnStyle, backgroundColor: '#1a56a0', border: 'none', padding: '0.75rem 1.5rem' }}
            >
              Booking Manager
            </button>
            <button
              onClick={handleLogout}
              style={{ ...btnStyle, backgroundColor: 'var(--btn-danger-bg)', border: 'none', padding: '0.75rem 1.5rem' }}
            >
              Logout
            </button>
          </div>
        </div>

        {/* Main panel */}
        <div style={{
          backgroundColor: 'var(--surface-1)',
          padding: '2rem',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border-default)'
        }}>
          <DashboardStats />

          {/* Quick Actions */}
          <div style={{ marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid var(--border-default)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ color: 'var(--text-primary)', margin: 0 }}>Quick Actions</h2>
              {statusMessage && (
                <span style={{
                  padding: '0.5rem 1rem',
                  borderRadius: 'var(--radius-md)',
                  fontSize: '0.9rem',
                  backgroundColor: statusMessage.type === 'success' ? 'var(--color-success)' :
                    statusMessage.type === 'error' ? 'var(--color-danger)' : 'var(--accent-blue)'
                }}>
                  {statusMessage.text}
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
              <button style={btnStyle} onClick={() => setShowDriverModal(true)}>Drivers</button>
              <button style={btnStyle} onClick={() => setShowSwapPreview(true)}>Swap Preview</button>
              <button style={btnStyle} onClick={() => setShowTruckModal(true)}>Trucks</button>
              <button style={btnStyle} onClick={() => setShowTrailerModal(true)}>Trailers</button>
              <button style={btnStyle} onClick={() => setShowCombinationsModal(true)}>Combinations</button>
              <button style={btnStyle} onClick={() => setCurrentView('equipment')}>Equipment Assignment</button>
              <button style={btnStyle} onClick={() => setShowFleetMap(true)}>
                Fleet Map
                {!isGPSConfigured() && warningDot}
              </button>
              <button style={btnStyle} onClick={() => setShowFuelStopModal(true)}>Fuel</button>
              <button style={btnStyle} onClick={() => setShowMaintenanceModal(true)}>Log Maintenance</button>
              <button
                onClick={() => setShowAlertInbox(true)}
                style={{ ...btnStyle, backgroundColor: inboxCount > 0 ? 'var(--color-warning)' : 'var(--btn-secondary-bg)', color: inboxCount > 0 ? '#000' : 'var(--text-primary)' }}
              >
                Alerts
                {inboxCount > 0 && (
                  <span style={{ marginLeft: '0.4rem', backgroundColor: '#000', color: 'var(--color-warning)', padding: '0.1rem 0.5rem', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 'bold' }}>
                    {inboxCount}
                  </span>
                )}
              </button>
              <button style={btnStyle} onClick={() => setShowNotificationCenter(true)}>
                Notifications
                {!isWhatsAppConfigured() && warningDot}
              </button>
              <button
                onClick={() => setShowRedAlertPanel(true)}
                style={{
                  ...btnStyle,
                  backgroundColor: unprocessedCount > 0 ? 'var(--color-danger)' : 'var(--btn-secondary-bg)',
                  animation: unprocessedCount > 0 ? 'pulse 2s infinite' : 'none'
                }}
              >
                Red Alerts
                {unprocessedCount > 0 && (
                  <span style={{
                    backgroundColor: 'var(--text-primary)',
                    color: 'var(--color-danger)',
                    padding: '0.1rem 0.5rem',
                    borderRadius: '10px',
                    fontSize: '0.75rem',
                    fontWeight: 'bold'
                  }}>
                    {unprocessedCount}
                  </span>
                )}
              </button>
              <button style={btnStyle} onClick={() => setShowActivityModal(true)}>Activity Log</button>
              <button style={btnStyle} onClick={() => setShowBIDashboard(true)}>BI Dashboard</button>
              <button
                onClick={handleExportLoads}
                disabled={exporting}
                style={{ ...btnStyle, opacity: exporting ? 0.6 : 1, cursor: exporting ? 'not-allowed' : 'pointer' }}
              >
                {exporting ? 'Exporting...' : 'Export Loads'}
              </button>
              {isOwner && (
                <button style={btnStyle} onClick={() => setShowSettingsModal(true)}>
                  Settings
                  {(!isWhatsAppConfigured() || !isAIConfigured()) && warningDot}
                </button>
              )}
            </div>
          </div>

          {/* View Switcher */}
          <div style={{
            display: 'flex',
            gap: '0.5rem',
            marginBottom: '1.5rem',
            padding: '0.75rem',
            backgroundColor: 'var(--surface-2)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--border-default)'
          }}>
            <span style={{ color: 'var(--text-secondary)', marginRight: '0.5rem', alignSelf: 'center', fontSize: '0.9rem' }}>View:</span>
            <button style={viewBtnStyle('kanban')} onClick={() => setCurrentView('kanban')}>Kanban</button>
            <button style={viewBtnStyle('timeline')} onClick={() => setCurrentView('timeline')}>Timeline</button>
            <button style={viewBtnStyle('daygraph')} onClick={() => setCurrentView('daygraph')}>Day graph</button>
            {/* __PLANNING__ sort the open work into lists first, then sequence a driver's day in the Route Builder */}
            <button style={viewBtnStyle('planning')} onClick={() => setCurrentView('planning')}>Planning</button>
            <button style={viewBtnStyle('routes')} onClick={() => setCurrentView('routes')}>Route Builder</button>
            <button style={viewBtnStyle('reports')} onClick={() => setCurrentView('reports')}>Reports</button>
            <button style={viewBtnStyle('archive')} onClick={() => setCurrentView('archive')}>Archive</button>
            <button style={viewBtnStyle('maintenance')} onClick={() => setCurrentView('maintenance')}>Maintenance</button>
            <button style={viewBtnStyle('fleet')} onClick={() => setCurrentView('fleet')}>Fleet Tracking</button>
            <button style={viewBtnStyle('equipment')} onClick={() => setCurrentView('equipment')}>Equipment Assignment</button>
          </div>

          {/* Views */}
          {currentView === 'kanban' && <KanbanBoard />}
          {currentView === 'timeline' && <TimelineView embedded={true} />}
          {currentView === 'daygraph' && <DayGraphView userId={user && user.id} />}
          {currentView === 'reports' && <ReportsModal embedded={true} onClose={() => setCurrentView('kanban')} />}
          {currentView === 'planning' && <PlanningBoard />}
          {currentView === 'routes' && (
            <div style={{ backgroundColor: 'var(--bg-primary)', borderRadius: 'var(--radius-lg)', height: 'calc(100vh - 350px)', minHeight: '500px', overflow: 'hidden' }}>
              <RouteBuilder />
            </div>
          )}
          {currentView === 'archive' && (
            <div style={{ height: 'calc(100vh - 350px)', minHeight: '500px' }}>
              <CompletedJobsArchive onCloneJob={() => setCurrentView('kanban')} />
            </div>
          )}
          {currentView === 'maintenance' && (
            <div style={{ height: 'calc(100vh - 350px)', minHeight: '500px' }}>
              <MaintenanceView />
            </div>
          )}
          {currentView === 'fleet' && <FleetTrackingView />}
          {currentView === 'equipment' && <EquipmentAssignmentView />}
        </div>

        {/* Footer */}
        <div style={{ marginTop: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
          Fleet Management System
        </div>
      </div>

      {/* Modals */}
      {showTruckModal && <TruckManagementModal onClose={() => setShowTruckModal(false)} onSuccess={() => {}} />}
      {showTrailerModal && <TrailerManagementModal onClose={() => setShowTrailerModal(false)} onSuccess={() => {}} />}
      {showCombinationsModal && <FleetCombinationsModal onClose={() => setShowCombinationsModal(false)} onSuccess={() => {}} />}
      {showDriverModal && <DriverManagementModal onClose={() => setShowDriverModal(false)} onSuccess={() => {}} />}
      {showSwapPreview && <SwapPreviewModal onClose={() => setShowSwapPreview(false)} />}
      {showFleetMap && <FleetMapModal onClose={() => setShowFleetMap(false)} />}
      {showAlertInbox && <AlertInbox onClose={() => setShowAlertInbox(false)} onChanged={() => countInbox().then(setInboxCount)} />}
      {showSettingsModal && <SettingsModal onClose={() => setShowSettingsModal(false)} />}
      {showNotificationCenter && <NotificationCenter onClose={() => setShowNotificationCenter(false)} />}
      {showMaintenanceModal && <MaintenanceModal onClose={() => setShowMaintenanceModal(false)} />}
      {showFuelStopModal && <FuelStopModal onClose={() => setShowFuelStopModal(false)} />}
      {showBIDashboard && <BIDashboardModal onClose={() => setShowBIDashboard(false)} />}
      {showActivityModal && <DispatcherActivityModal onClose={() => setShowActivityModal(false)} />}
      {showRedAlertPanel && (
        <RedAlertPanel
          customers={customers}
          onClose={() => {
            setShowRedAlertPanel(false);
            setUnprocessedCount(getUnprocessedMessages().length);
          }}
          onCreateLoad={(draftLoad) => console.log('Draft load created:', draftLoad)}
        />
      )}

      <style>{`
        @keyframes pulse {
          0% { box-shadow: 0 0 0 0 rgba(231, 76, 60, 0.7); }
          70% { box-shadow: 0 0 0 10px rgba(231, 76, 60, 0); }
          100% { box-shadow: 0 0 0 0 rgba(231, 76, 60, 0); }
        }
      `}</style>
    </div>
  );
}

export default Dashboard;
